import type { NatureContent } from "@/pages/bali-info-detail/data/types";
import { plantPhotos } from "@/pages/bali-info-detail/data/plantPhotos";

export const plantsContent: NatureContent = {
  intro: {
    de: "Reisende bewundern immer wieder die Vielfältigkeit und Farbenpracht exotischer Pflanzen. Namen und – bei Nutzpflanzen – der genaue Verwendungszweck bleiben dabei meist unbekannt. Auf den folgenden Seiten bemühen wir uns, den Pflanzenreichtum Balis zu erklären. So manche Pflanze steht vielleicht auch in Ihrem Haus oder ist in der heimischen Gärtnerei wieder zu erkennen.",
    en: "Travellers are endlessly fascinated by the variety and colour of tropical plants – yet the names, and for useful plants the exact purpose, usually remain unknown. On the following pages we try to explain the plant wealth of Bali. You may even recognise some of them from your own home or local garden centre.",
  },
  namingHint: {
    de: "In unserer Beschreibung steht zuerst der deutsche, dann der indonesische und klein geschrieben der lateinische Name.",
    en: "In our descriptions the German name comes first, then the Indonesian one, and in small italics the Latin name.",
  },
  groups: [
    {
      title: { de: "Nutzpflanzen 01", en: "Useful plants 01" },
      image:
        "https://readdy.ai/api/search-image?query=Cassava%20roots%2C%20taro%20and%20bamboo%20shoots%20arranged%20on%20a%20woven%20mat%20at%20a%20Balinese%20village%20market%2C%20natural%20daylight%2C%20earthy%20tones%2C%20documentary%20food%20photography&width=1200&height=600&seq=bali-plants-group-nutzpflanzen-01&orientation=landscape",
      imageAlt: {
        de: "Maniok, Taro und Bambussprossen auf einer geflochtenen Matte am Markt",
        en: "Cassava, taro and bamboo shoots on a woven mat at the market",
      },
      text: {
        de: "Maniok sowie verschiedene Knollenfrüchte und Gemüsesorten sind heute unentbehrlich, um die Menschen der Insel zu ernähren. Bambus ist allgegenwärtig und eines der wichtigsten Rohstoffe. Verschiedene Bäume werden als heilig verehrt, haben religiöse Verwendung und sind gute Schattenspender.",
        en: "Cassava, various root crops and vegetables are indispensable for feeding the island's people today. Bamboo is everywhere and one of the most important raw materials. Certain trees are worshipped as sacred, used in rituals and give welcome shade.",
      },
      entries: [
        {
          name: { de: "Bambus", en: "Bamboo" },
          local: "Bambu",
          latin: "dendrocalamus giganteus",
          text: {
            de: "Bambus zählt zu den Gräsern und ist die größte Art seiner Gattung. Die Stöcke werden viele Jahrzehnte alt, die Halme können jedoch regelmäßig nach ein bis zwei Jahren geerntet werden. Bambus wächst in der Endstärke aus dem Boden – einige Sorten legen in der ersten Periode 30 bis 50 cm am Tag zu. Riesenbambus erreicht bis zu 30 Meter Höhe und blüht nur sehr selten, oft erst zwischen 50 und 100 Jahren. In Asien findet Bambus in allen Lebensbereichen Verwendung und ist eines der schnellstwachsenden Grundmaterialien, die dem Menschen zur Verfügung stehen.",
            en: "Bamboo belongs to the grasses and is the largest species of its genus. The clumps live for many decades, yet the culms can be harvested regularly after one or two years. Bamboo emerges from the ground at its final thickness – some varieties gain 30 to 50 cm a day in their first period. Giant bamboo reaches 30 metres and flowers only very rarely, often after 50 to 100 years. In Asia bamboo is used in every area of life and is one of the fastest-growing raw materials available to mankind.",
          },
          photos: plantPhotos.bambus,
        },
        {
          name: { de: "Chayote", en: "Chayote" },
          local: "Jepang",
          latin: "sechium edule",
          text: {
            de: "Die geschmacklosen Früchte dieser schnellwachsenden Kürbispflanze sind heute auf Bali ein wichtiges Gemüse. Anbaugebiete sind vorwiegend die Bergregionen der Insel – die Ranken klettern dort an Bambusgerüsten empor.",
            en: "The bland fruits of this fast-growing squash are an important vegetable on Bali today. It is grown mainly in the island's mountain regions, where the vines climb up bamboo frames.",
          },
          photos: plantPhotos.chayote,
        },
        {
          name: { de: "Erdnuss", en: "Peanut" },
          local: "Kacang Tanah",
          latin: "arachis hypogaea",
          text: {
            de: "Die Erdnuss wächst als niedrige Staude und schiebt ihre Schoten nach der Blüte selbst in die Erde, wo sie reifen. Auf Bali werden die Nüsse geröstet oder – in Gelbwurz gekocht – als gelber Straßensnack verkauft.",
            en: "The peanut grows as a low shrub and pushes its pods into the soil after flowering, where they ripen. On Bali the nuts are roasted or boiled with turmeric and sold as a yellow street snack.",
          },
          photos: plantPhotos.erdnuss,
        },
        {
          name: { de: "Süßkartoffel", en: "Sweet potato" },
          local: "Katela rambat",
          latin: "ipomoea batatas",
          text: {
            de: "Rankende Pflanze mit rötlichen oder violetten Knollen. Sie gedeiht auch auf mageren Böden und wird gerne am Straßenrand als Snack frittiert verkauft. In der balinesischen Küche ist sie Beilage und Suppeneinlage zugleich.",
            en: "A climbing plant with reddish or purple tubers. It thrives even on poor soil and is often sold fried as a street snack. In Balinese cooking it appears both as a side dish and in soups.",
          },
          photos: plantPhotos.suesskartoffel,
        },
        {
          name: { de: "Maniok", en: "Cassava" },
          local: "Katela Pohon",
          latin: "manihot esculenta",
          text: {
            de: "Der Strauch mit den handförmigen Blättern wächst fast überall auf Bali. Die stärkehaltigen Wurzeln werden gekocht, frittiert oder zu Mehl verarbeitet; auch die jungen Blätter kommen als Gemüse auf den Tisch. Roh sind die Knollen giftig – sie müssen immer gut gegart werden.",
            en: "The shrub with hand-shaped leaves grows almost everywhere on Bali. Its starchy roots are boiled, fried or ground into flour, and the young leaves are eaten as a vegetable. Raw roots are toxic and must always be cooked thoroughly.",
          },
          photos: plantPhotos.maniok,
        },
        {
          name: { de: "Tania", en: "Tannia" },
          local: "Keladi",
          latin: "xanthosoma sagittifolium",
          text: {
            de: "Eine der ältesten Kulturpflanzen der Tropen. Die großen, pfeilförmigen Blätter sind ein typisches Motiv in der balinesischen Kunst. Die stärkereichen Knollen schmecken leicht nussig und werden gedämpft, in Suppen oder als Beilage serviert.",
            en: "One of the oldest cultivated plants of the tropics. Its large arrow-shaped leaves are a classic motif in Balinese art. The starchy tubers taste mildly nutty and are served steamed, in soups or as a side dish.",
          },
          photos: plantPhotos.tania,
        },
      ],
    },
    {
      title: { de: "Nutzpflanzen 02", en: "Useful plants 02" },
      image:
        "https://readdy.ai/api/search-image?query=Tall%20coconut%20palms%20and%20sugar%20palms%20along%20a%20quiet%20Bali%20coastline%2C%20soft%20hazy%20light%2C%20deep%20green%20fronds%20against%20a%20pale%20sky%2C%20calm%20tropical%20landscape%20photography&width=1200&height=600&seq=bali-plants-group-palmen-01&orientation=landscape",
      imageAlt: {
        de: "Kokospalmen und Zuckerpalmen an der Küste Balis",
        en: "Coconut and sugar palms along the Bali coast",
      },
      text: {
        de: "Kokosnüsse, Zuckerpalmen und Reis zählen zu den ältesten Kulturpflanzen der Menschen in den Tropen und dienen als Nahrungsmittel. Sie liefern Öl, Zucker und Baustoff – und sie werfen Schatten auf jedes Dorf.",
        en: "Coconuts, sugar palms and rice are among the oldest cultivated plants in the tropics and serve as staple food. They provide oil, sugar and building material – and shade for every village.",
      },
      entries: [
        {
          name: { de: "Kokospalme", en: "Coconut palm" },
          local: "Kelapa",
          latin: "cocos nucifera",
          text: {
            de: "Bis zu 30 Meter hoch und überall an der Küste und im Binnenland zu finden. Grüne Nüsse liefern Trinkwasser und weiches Fleisch, reife braune Nüsse das Öl. Fasern, Schalen und Blätter werden vollständig weiterverwertet – von der Matte bis zum Opfergefäß.",
            en: "Up to 30 metres tall and found along the coast and inland. Green nuts provide drinking water and soft flesh, mature brown nuts the cooking oil. Fibres, shells and leaves are used in full – from mats to offering bowls.",
          },
          photos: plantPhotos.kokospalme,
        },
        {
          name: { de: "Zuckerpalme", en: "Sugar palm" },
          local: "Aren",
          latin: "arenga pinnata",
          text: {
            de: "Aus dem Blütenstand wird der Palmzucker (Gula Aren) gewonnen, der in unzähligen kleinen Hütten über offenem Feuer eingekocht wird. Der Palmwein Tuak und der Schnaps Arrak entstehen ebenfalls aus dem süßen Saft.",
            en: "Its flower stalk yields palm sugar, boiled down over open fires in countless small huts. The sweet sap is also the source of palm wine (tuak) and the spirit arak.",
          },
          photos: plantPhotos.zuckerpalme,
        },
        {
          name: { de: "Reis", en: "Rice" },
          local: "Padi",
          latin: "oryza sativa",
          text: {
            de: "Reis ist das Grundnahrungsmittel Balis und prägt die Landschaft wie keine andere Pflanze. In den terrassierten Feldern rund um Mengwi werden pro Jahr zwei bis drei Ernten eingebracht. Nach der Ernte wird das Stroh getrocknet und als Futter oder Dachmaterial verwendet.",
            en: "Rice is Bali's staple food and shapes the landscape like no other plant. The terraced fields around Mengwi produce two to three harvests a year. After harvesting, the straw is dried and used as fodder or roofing material.",
          },
        },
      ],
    },
    {
      title: { de: "Tropische Bäume", en: "Tropical trees" },
      image:
        "https://readdy.ai/api/search-image?query=Huge%20old%20pule%20tree%20with%20a%20broad%20canopy%20and%20red%20flamboyant%20blossoms%20beside%20a%20Balinese%20village%20lane%2C%20warm%20late%20afternoon%20light%2C%20lush%20green%20surroundings&width=1200&height=600&seq=bali-plants-group-baeume-01&orientation=landscape",
      imageAlt: {
        de: "Alter Pule-Baum und rot blühender Flamboyan an einem Dorfweg",
        en: "Old pule tree and red flamboyant blossom beside a village lane",
      },
      text: {
        de: "Riesenbäume überragen Häuser und Tempel – sie gelten als heilig. Rotblühende Flamboyanbäume zieren Parkanlagen, Straßen und Dörfer, vor allem am Ende der Regenzeit.",
        en: "Giant trees tower over houses and temples and are considered sacred. Red-flowering flamboyant trees decorate parks, roads and villages, especially at the end of the rainy season.",
      },
      entries: [
        {
          name: { de: "Flamboyan", en: "Flamboyant tree" },
          local: "Flamboyan",
          latin: "delonix regia",
          text: {
            de: "Sein dichtes, feines Laub bildet eine breite Krone, die im November und Dezember in leuchtendes Rot getaucht ist. Als Schattenbaum in Gärten und Straßen gepflanzt, ist er ein beliebtes Fotomotiv.",
            en: "Its fine, dense foliage forms a broad crown that turns brilliant red in November and December. Planted as a shade tree in gardens and streets, it is a favourite photo motif.",
          },
          photos: plantPhotos.flamboyan,
        },
        {
          name: { de: "Taep (Pule)", en: "Taep tree" },
          local: "Pohon Taep",
          latin: "alstonia scholaris",
          text: {
            de: "Der hochstämmige Baum mit den quirlig angeordneten Blättern gilt auf Bali als heilig. Seine weißen Blüten duften in der Nacht; alte Exemplare stehen oft neben Tempeln und markieren den Dorfmittelpunkt.",
            en: "This tall tree with whorled leaves is considered sacred on Bali. Its white flowers are fragrant at night, and old specimens often stand beside temples, marking the village centre.",
          },
          photos: plantPhotos.taep,
        },
        {
          name: { de: "Frangipani", en: "Frangipani" },
          local: "Kemboja",
          latin: "plumeria (apocynaceae)",
          text: {
            de: "Die kleinen Bäume mit ihren glänzenden, fleischigen Blüten gehören zu jeder Zeremonie. Es gibt sie in Weiß, Rosa, Rot und Gelb. Die Blüten werden in Opferschalen gelegt, hinter das Ohr gesteckt oder als Willkommensring gereicht.",
            en: "These small trees with glossy, waxy flowers belong to every ceremony, in white, pink, red and yellow. The blossoms are arranged in offerings, worn behind the ear or handed as a welcome garland.",
          },
          photos: plantPhotos.frangipani,
        },
      ],
    },
    {
      title: { de: "Stauden und Zierpflanzen 01", en: "Shrubs and ornamentals 01" },
      image:
        "https://readdy.ai/api/search-image?query=Heliconia%20and%20hibiscus%20blooms%20in%20a%20lush%20Balinese%20garden%2C%20bright%20red%20and%20orange%20bracts%2C%20soft%20diffused%20light%2C%20vivid%20tropical%20botanical%20photography&width=1200&height=600&seq=bali-plants-group-zierpflanzen-01&orientation=landscape",
      imageAlt: {
        de: "Helikonien und Hibiskusblüten in einem balinesischen Garten",
        en: "Heliconia and hibiscus blooms in a Balinese garden",
      },
      text: {
        de: "Die Balier sind Meister im Landschaftsbau. Neben dem kunstvollen Reisanbau gestalten sie ihre Gärten mit farbenprächtigen Bäumen und Staudenarten. Da wegen des gemäßigten Klimas fast alles wächst, ist die Bandbreite sehr groß. Viele Blüten werden für die täglichen Opferschälchen verwendet.",
        en: "Balinese gardeners are masters of landscaping. Alongside their artful rice farming they shape gardens with brilliantly coloured trees and shrubs. With the mild climate almost everything grows, so the range is huge – and many flowers end up in the daily offerings.",
      },
      entries: [
        {
          name: { de: "Wüstenrose", en: "Desert rose" },
          local: "Adenium",
          latin: "adenium obesum",
          text: {
            de: "Beliebte Topfpflanze Balis, die häufig in den Höfen der Häuser aufgestellt wird. Der verdickte Stamm speichert Wasser, die trompetenförmigen Blüten erscheinen fast das ganze Jahr in Rosa und Rot.",
            en: "A popular pot plant on Bali, often placed in house courtyards. Its swollen trunk stores water and the trumpet-shaped flowers appear in pink and red almost all year round.",
          },
          photos: plantPhotos.wuestenrose,
        },
        {
          name: { de: "Engelstrompete", en: "Angel's trumpet" },
          local: "Kecubung",
          latin: "datura arborea",
          text: {
            de: "Die stark riechenden, giftigen Zierpflanzen wachsen in den kühleren Bergregionen Balis. Ihre hängenden, bis 30 cm langen Blüten öffnen sich abends und verströmen dann ihren Duft über den ganzen Hof.",
            en: "These heavily scented, poisonous ornamentals grow in Bali's cooler mountain regions. Their hanging flowers, up to 30 cm long, open in the evening and then fill the whole courtyard with fragrance.",
          },
          photos: plantPhotos.engelstrompete,
        },
        {
          name: { de: "Helikonie", en: "Heliconia" },
          local: "Pisang Merah",
          latin: "heliconia",
          text: {
            de: "Ihre leuchtend roten, gelben oder orangen Hochblätter sehen aus wie Federbuschen. Sie sind das lebende Erkennungszeichen vieler balinesischer Gärten und halten sich in der Vase sehr lange.",
            en: "Its bright red, yellow or orange bracts look like feather dusters. A living trademark of many Balinese gardens, they also keep remarkably long in a vase.",
          },
          photos: plantPhotos.helikonie,
        },
        {
          name: { de: "Roseneibisch", en: "Hibiscus" },
          local: "Kembang Sepatu",
          latin: "hibiscus rosa-sinensis",
          text: {
            de: "Die große, trichterförmige Blüte ist die Nationalblume Malaysias und auf Bali in Rot, Rosa, Gelb und Weiß zu sehen. Sie blüht täglich neu und eignet sich daher gut für Opfergaben.",
            en: "Its large funnel-shaped flower is Malaysia's national bloom and appears on Bali in red, pink, yellow and white. It opens a new flower every day, which makes it ideal for offerings.",
          },
          photos: plantPhotos.hibiskus,
        },
      ],
    },
    {
      title: { de: "Stauden und Zierpflanzen 02", en: "Shrubs and ornamentals 02" },
      image:
        "https://readdy.ai/api/search-image?query=Fan-shaped%20travellers%20palm%20and%20a%20large%20staghorn%20fern%20in%20a%20lush%20tropical%20Balinese%20garden%2C%20broad%20green%20leaves%2C%20soft%20daylight%2C%20botanical%20photography&width=1200&height=600&seq=bali-plants-group-zierpflanzen-02-01&orientation=landscape",
      imageAlt: {
        de: "Fächerpalme und Geweihfarn in einem tropischen Garten",
        en: "Traveller's palm and staghorn fern in a tropical garden",
      },
      text: {
        de: "Immer wieder finden auf Bali Landschaftswettbewerbe statt, und viele Balier pflegen ihre Vorgärten mit besonderer Emsigkeit. Die Insel gleicht in vielen Gebieten einem riesigen Park.",
        en: "Landscaping competitions are held on Bali again and again, and many Balinese tend their front gardens with remarkable diligence. In many areas the island resembles one huge park.",
      },
      entries: [
        {
          name: { de: "Fächerpalme (Baum der Reisenden)", en: "Traveller's palm" },
          local: "Ravenala",
          latin: "ravenala madagascariensis",
          text: {
            de: "Ihre Blätter stehen wie ein aufgespannter Fächer in einer Ebene. In den Blattscheiden sammelt sich Regenwasser, das Wanderern früher als Trinkwasser diente. Sie ist keine echte Palme, sondern mit der Strelitzie verwandt.",
            en: "Its leaves stand in a single plane like an opened fan. Rainwater collects in the leaf sheaths and once served travellers as drinking water. It is not a true palm but related to the bird of paradise flower.",
          },
          photos: plantPhotos.ravenala,
        },
        {
          name: { de: "Geweihfarn", en: "Staghorn fern" },
          local: "Tanduk Rusa",
          latin: "platycerium",
          text: {
            de: "Der Aufsitzerfarn lebt auf Bäumen und Mauern, ohne ihnen zu schaden. Seine gegabelten Wedel erinnern an ein Hirschgeweih – daher der indonesische Name Tanduk Rusa. Die unteren, schildförmigen Blätter sammeln Laub und Feuchtigkeit.",
            en: "This epiphytic fern lives on trees and walls without harming them. Its forked fronds recall a pair of antlers – hence the Indonesian name Tanduk Rusa. The lower shield-shaped leaves collect leaf litter and moisture.",
          },
          photos: plantPhotos.geweihfarn,
        },
      ],
    },
    {
      title: { de: "Stauden und Zierpflanzen 03", en: "Shrubs and ornamentals 03" },
      image:
        "https://readdy.ai/api/search-image?query=Red%20cordyline%20leaves%20beside%20dense%20orange%20pagoda%20flower%20clusters%20in%20a%20Balinese%20temple%20garden%2C%20rich%20saturated%20colours%2C%20soft%20shadow%20light%2C%20botanical%20detail%20photography&width=1200&height=600&seq=bali-plants-group-zierpflanzen-03-01&orientation=landscape",
      imageAlt: {
        de: "Rote Keulenlilie und orange Pagodenblüten in einem Tempelgarten",
        en: "Red cordyline and orange pagoda flowers in a temple garden",
      },
      text: {
        de: "Das feuchttropische Klima Balis gibt den Pflanzen ideale Bedingungen, das ganze Jahr über zu gedeihen. Manche Bäume wie der Flamboyan blühen nur während der Regenzeit, andere lassen in der Trockenzeit ihre Blätter fallen.",
        en: "Bali's humid tropical climate gives plants ideal conditions to thrive all year round. Some trees, like the flamboyant, flower only during the rainy season, while others drop their leaves in the dry season.",
      },
      entries: [
        {
          name: { de: "Keulenlilie", en: "Cordyline" },
          local: "Andong",
          latin: "cordyline fruticosa",
          text: {
            de: "Der Strauch mit den langen, schwertförmigen Blättern erscheint in Rot, Rosa und Grün. Auf Bali gilt er als heilige Pflanze: Die roten Blätter gehören zu fast jeder Tempelzeremonie und markieren Hauseingänge.",
            en: "This shrub with long sword-shaped leaves appears in red, pink and green. On Bali it counts as a sacred plant: the red leaves feature in almost every temple ceremony and mark house entrances.",
          },
          photos: plantPhotos.keulenlilie,
        },
        {
          name: { de: "Pagodenblume", en: "Pagoda flower" },
          local: "Kembang Pagoda",
          latin: "clerodendrum paniculatum",
          text: {
            de: "Der Strauch mit den dichten, etagenartig aufgebauten Blütenständen in Rot und Orange blüht fast das ganze Jahr. Er wird gerne als Begrenzung von Wegen und Tempelhöfen gesetzt.",
            en: "This shrub has dense, tiered flower heads in red and orange and blooms almost all year. It is often planted to edge paths and temple courtyards.",
          },
          photos: plantPhotos.pagodenblume,
        },
      ],
    },
    {
      title: { de: "Wasserpflanzen", en: "Water plants" },
      image:
        "https://readdy.ai/api/search-image?query=Pink%20lotus%20flowers%20and%20round%20leaves%20in%20a%20still%20temple%20pond%20on%20Bali%2C%20stone%20edge%2C%20soft%20morning%20light%2C%20serene%20atmosphere&width=1200&height=600&seq=bali-plants-group-wasserpflanzen-01&orientation=landscape",
      imageAlt: {
        de: "Lotusblüten in einem stillen Tempelteich auf Bali",
        en: "Lotus flowers in a still temple pond on Bali",
      },
      text: {
        de: "Indischer Lotus ist das Symbol asiatischer Mystik schlechthin und findet in der hinduistischen und buddhistischen Religion seine große Bedeutung als Pflanze der drei Elemente Erde – Wasser – Luft, Zeichen der Trinität.",
        en: "The Indian lotus is the very symbol of Asian mysticism and holds great significance in Hindu and Buddhist religion as the plant of the three elements earth – water – air, a sign of the trinity.",
      },
      entries: [
        {
          name: { de: "Indischer Lotus", en: "Indian lotus" },
          local: "Teratai",
          latin: "nelumbo nucifera",
          text: {
            de: "Die Pflanze wurzelt im Schlamm, wächst durch das Wasser und blüht über der Oberfläche – deshalb gilt sie als Sinnbild für Reinheit und Erleuchtung. In den Tempelteichen Balis blüht sie in Rosa und Weiß.",
            en: "The plant roots in the mud, grows through the water and flowers above the surface – which is why it symbolises purity and enlightenment. In Bali's temple ponds it blooms in pink and white.",
          },
          photos: plantPhotos.lotus,
        },
        {
          name: { de: "Ägyptischer Lotus (Seerose)", en: "Egyptian lotus (water lily)" },
          local: "Teratai Air",
          latin: "nymphaea",
          text: {
            de: "Anders als der Lotus schwebt die Seerose mit ihren Blättern und Blüten frei auf der Wasseroberfläche. Ihre Blüten öffnen sich morgens und schließen sich am Nachmittag wieder.",
            en: "Unlike the lotus, the water lily floats freely on the surface with both leaves and flowers. Its blooms open in the morning and close again in the afternoon.",
          },
          photos: plantPhotos.seerose,
        },
      ],
    },
    {
      title: { de: "Kletterpflanzen", en: "Climbing plants" },
      image:
        "https://readdy.ai/api/search-image?query=Magenta%20bougainvillea%20spilling%20over%20a%20whitewashed%20garden%20wall%20in%20Bali%2C%20golden%20trumpet%20flowers%20nearby%2C%20warm%20sunlight%2C%20editorial%20tropical%20photography&width=1200&height=600&seq=bali-plants-group-kletterpflanzen-01&orientation=landscape",
      imageAlt: {
        de: "Bougainvillea über einer weiß getünchten Gartenmauer",
        en: "Bougainvillea spilling over a whitewashed garden wall",
      },
      text: {
        de: "Viele Pflanzen der Tropen nutzen andere Baumstämme oder Mauern, um an ihnen hochzuklettern und ihre Blütenpracht zu zeigen. Wer kennt heute nicht die Bougainvillea mit ihren kräftigen Farben.",
        en: "Many tropical plants use other trunks or walls to climb upwards and display their blossoms there. Who doesn't know the bougainvillea with its strong colours.",
      },
      entries: [
        {
          name: { de: "Bougainvillea", en: "Bougainvillea" },
          local: "Bunga Kertas",
          latin: "bougainvillea",
          text: {
            de: "Ihre eigentlichen Blüten sind unscheinbar – die kräftigen Farben stammen von den papierartigen Hochblättern, die ihr den Namen „Papierblume“ gaben. Sie blüht umso üppiger, je trockener der Standort.",
            en: "The true flowers are insignificant – the strong colours come from papery bracts, which earned it the name 'paper flower'. The drier the spot, the more abundantly it blooms.",
          },
          photos: plantPhotos.bougainvillea,
        },
        {
          name: { de: "Goldtrompete", en: "Golden trumpet" },
          local: "Alamanda",
          latin: "allamanda cathartica",
          text: {
            de: "Ihre goldgelben, trompetenförmigen Blüten leuchten an Zäunen und Mauern. Die Pflanze ist giftig – ein guter Grund, sie nur anzuschauen und nicht zu pflücken.",
            en: "Its golden trumpet-shaped flowers glow along fences and walls. The plant is toxic – a good reason to admire it rather than pick it.",
          },
          photos: plantPhotos.goldtrompete,
        },
        {
          name: { de: "Passionsblume", en: "Passion flower" },
          local: "Markisa",
          latin: "passiflora",
          text: {
            de: "Die außergewöhnliche Blüte mit ihrem Strahlenkranz wirkt wie ein Kunstwerk. Aus den Früchten entsteht der säuerliche Maracujasaft, der auf Bali frisch gepresst serviert wird.",
            en: "The extraordinary flower with its crown of rays looks like a work of art. Its fruit becomes the tart passion fruit juice served freshly pressed on Bali.",
          },
        },
      ],
    },
  ],
  closing: {
    de: "Wer mit offenen Augen durch die Reisfelder von Mengwi geht, entdeckt auf wenigen hundert Metern mehr Pflanzenarten als in manchem botanischen Garten. Gery zeigt sie auf den Touren gerne persönlich – und erklärt, welche davon auf den Tisch kommen.",
    en: "Anyone walking through the rice fields of Mengwi with open eyes will spot more plant species in a few hundred metres than in many a botanical garden. Gery is happy to point them out personally on our tours – and explain which of them end up on the table.",
  },
};